import { FC, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { Modal } from '@savgroup-front-common/core/atoms/modal';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { SUPPORTED_ICON_POSITIONS } from '@savgroup-front-common/core/helpers';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { ICONS_TYPE } from '@savgroup-front-common/types';

import { useGetClaimGroupConfirmation } from '@/view/app/hooks/useGetClaimGroupConfirmation';
import { useGetSolutionsByClaim } from '@/view/app/hooks/useGetSolutionsByClaim';
import {
  PROFESSIONAL_STEP_CONFIG,
  PROFESSIONAL_STEP_PROGRESS,
} from '@/view/app/NewLayout/ProfessionalRoutes.config';

import { PageFooterContainer } from '../../../../../app/NewLayout/NewStepLayout';
import { NewStepLayoutProfessional } from '../../../../../app/NewLayout/NewStepLayout/NewStepLayoutProfessional';
import { ResumeCard } from '../../../components/ResumeCard';
import { IrshStepValues } from '../../IrshPages.types';
import useClaimGroupSolutionPage from '../ClaimGroupSolutionPage.hooks';
import {
  $Heading,
  $HeadingIcon,
  $LeftColumn,
  $SolutionItemWrapper,
  $SolutionResultWrapper,
  $SolutionWrapper,
} from '../ClaimGroupSolutionPage.styles';
import messages from '../messages';
import { SolutionItem } from '../SolutionItem';

interface ClaimGroupSolutionProps {
  onNextStep: RoutedStepProps<IrshStepValues>['onNextStep'];
  onPreviousStep: RoutedStepProps<IrshStepValues>['onPreviousStep'];
}

export const ClaimGroupSolution: FC<ClaimGroupSolutionProps> = ({
  onNextStep,
  onPreviousStep,
}) => {
  const { claimGroupId } = useParams<{ claimGroupId: string }>();

  const [openResumeModal, setOpenResumeModal] = useState(false);

  const { onSubmit, formContext } = useClaimGroupSolutionPage({
    onNextStep,
  });
  const { solutions, isSolutionsLoading } = useGetSolutionsByClaim({
    claimGroupId,
  });

  const { claimGroupConfirmation, isLoading } = useGetClaimGroupConfirmation({
    claimGroupId,
  });

  const handleOpenModal = () => setOpenResumeModal((prev) => !prev);

  const handlePreviousStep = () => {
    if (claimGroupConfirmation?.orderReference) {
      onPreviousStep({
        newValues: {
          orderReference: claimGroupConfirmation.orderReference,
        },
      });
    }
  };

  if (isLoading || isSolutionsLoading) {
    return <BaseLoader />;
  }

  return (
    <>
      <FormProvider {...formContext}>
        <form
          onSubmit={(event) => {
            event.preventDefault();

            return undefined;
          }}
        >
          <NewStepLayoutProfessional
            stepName={PROFESSIONAL_STEP_CONFIG.PROFESSIONAL_SOLUTION_PAGE}
            customSteps={PROFESSIONAL_STEP_PROGRESS}
          >
            <$SolutionWrapper>
              <$LeftColumn>
                <$Heading>
                  <$HeadingIcon data-testid="ClaimGroupSolutionHeadingIcon">
                    <MyAccountIcon icon={ICONS_TYPE.LIGHT_BULB_ON_ICON} />
                  </$HeadingIcon>
                  <SafeFormattedMessageWithoutSpread
                    message={messages.solutionTitle}
                  />
                </$Heading>
                <$SolutionItemWrapper data-testid="SolutionItemWrapper">
                  {solutions?.map((solution) => (
                    <SolutionItem
                      key={solution.key}
                      solution={solution}
                      onClick={async () => {
                        await onSubmit({
                          chooseSolution: solution.solutionTypeId,
                        });
                      }}
                    />
                  ))}
                </$SolutionItemWrapper>
              </$LeftColumn>

              <$SolutionResultWrapper>
                <ResumeCard />
              </$SolutionResultWrapper>
            </$SolutionWrapper>
          </NewStepLayoutProfessional>

          <PageFooterContainer reverseChildren={false}>
            <Button
              type={BUTTON_TYPES.BUTTON}
              tertiary
              disabled={!claimGroupConfirmation?.orderReference}
              onClick={() => {
                handlePreviousStep();
              }}
              icon={<ArrowNakedLeftIcon />}
              position={SUPPORTED_ICON_POSITIONS.LEFT}
              dataTestId="previousButton"
            >
              <SafeFormattedMessageWithoutSpread
                message={messages.previousButton}
              />
            </Button>
          </PageFooterContainer>
        </form>
      </FormProvider>

      <Modal
        isOpen={openResumeModal}
        onClose={handleOpenModal}
        shouldShowPadding={false}
      >
        <ResumeCard />
      </Modal>
    </>
  );
};
